import { useState } from 'react'
import {
  type AgentMatchCall,
  EMPTY_VALUE,
  type MatchCandidateRow,
  type MatchRowMark,
  type MatchSide,
} from '~/entities/document-agent'
import { cn } from '~/shared/lib/cn'
import { pluralize } from '~/shared/lib/pluralize'
import { MATCH_ROW_COLUMNS, MATCH_ROW_MARKS } from '../config'
import { formatMatchValue, getRowValue } from '../lib'
import { JsonBlock } from './json-block'
import { Muted } from './primitives'

const NOWRAP_COLUMNS = new Set<string>([
  'name',
  'date_of_birth',
  'patient_dob',
  'lims_date_of_service',
  'service_date',
])

const CELL = 'border-divider border-b px-2 py-1.5 align-top first:pl-3 last:pr-3'

type CandidateRow = {
  row: MatchCandidateRow
  handle: string
  mark: MatchRowMark | undefined
  call: number
}

function isMarked(mark: MatchRowMark | undefined): boolean {
  return !!mark && (mark.agent || mark.reviewer || mark.today || mark.tied || mark.rules)
}

/** Every row the agent's lookups returned, marked with who picked it; the marked rows first. */
export function MatchCandidatesTable({
  side,
  calls,
  marks,
  hasFinal,
}: {
  side: MatchSide
  calls: AgentMatchCall[]
  marks: MatchRowMark[][]
  /** The side has a settled final case, so an agent row that is not it is wrong (red). */
  hasFinal: boolean
}) {
  const [showAll, setShowAll] = useState(false)
  const columns = MATCH_ROW_COLUMNS[side]
  const rows: CandidateRow[] = calls.flatMap((call, callIndex) =>
    (call.lookup?.rows ?? []).map((row, rowIndex) => ({
      row,
      handle: call.handles?.[rowIndex] ?? `#${rowIndex + 1}`,
      mark: marks[callIndex]?.[rowIndex],
      call: callIndex,
    })),
  )
  const marked = rows.filter((item) => isMarked(item.mark))
  const visible = showAll || marked.length === 0 ? rows : marked
  const hidden = rows.length - visible.length
  const dropped = calls.some((call) => call.lookup?.rows_dropped)

  if (rows.length === 0) {
    return (
      <Muted className="px-4">
        {dropped
          ? 'The candidate rows were too large to store with the run; the matching transcript has them.'
          : 'The lookups returned no candidates.'}
      </Muted>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-left text-[12px] leading-5">
          <thead>
            <tr>
              {['Row', 'Picked by', ...columns.map((column) => column.label)].map((label) => (
                <th
                  key={label}
                  className="border-divider text-text-tertiary border-b px-2 py-1.5 text-left text-[11px] font-medium tracking-[0.4px] whitespace-nowrap uppercase first:pl-3 last:pr-3"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map(({ row, handle, mark, call }) => (
              <tr
                key={`${call}-${handle}`}
                className={cn({
                  'bg-alert-success-fill/60': mark?.reviewer,
                  'bg-alert-error-fill/60': hasFinal && mark?.agent && !mark.reviewer,
                  'bg-primary-selected/60': !hasFinal && mark?.agent,
                })}
              >
                <td className={cn(CELL, 'font-mono font-medium whitespace-nowrap')}>{handle}</td>
                <td className={CELL}>
                  <div className="flex gap-1">
                    {MATCH_ROW_MARKS.filter(({ key }) => mark?.[key]).map(
                      ({ key, label, description, className }) => (
                        <span
                          key={key}
                          title={description}
                          className={cn(
                            'inline-flex h-5 items-center rounded-full px-1.5 text-[11px] font-medium whitespace-nowrap',
                            className,
                          )}
                        >
                          {label}
                        </span>
                      ),
                    )}
                  </div>
                </td>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      CELL,
                      'min-w-[72px]',
                      NOWRAP_COLUMNS.has(column.key) ? 'whitespace-nowrap' : 'wrap-anywhere',
                    )}
                  >
                    {formatMatchValue(getRowValue(row, column.key)) ?? EMPTY_VALUE}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-3">
        {(hidden > 0 || showAll) && marked.length > 0 && marked.length < rows.length && (
          <button
            type="button"
            onClick={() => setShowAll((value) => !value)}
            className="text-text-link cursor-pointer border-none bg-transparent p-0 text-[12px] leading-4 hover:underline"
          >
            {showAll
              ? 'Show only the picked rows'
              : `Show all ${rows.length} ${pluralize(rows.length, 'candidate')}`}
          </button>
        )}
        <JsonBlock
          value={rows.map(({ handle, row }) => ({ handle, ...row }))}
          label="Raw rows"
          defaultOpen={false}
        />
      </div>
    </div>
  )
}
